import { useState, useEffect } from 'react';
import api from '../api';
import { useAuth } from '../context/AuthContext';
import PaymentDetail from '../components/PaymentDetail';
import { downloadExcel } from '../utils/excelExport';

const TYPES = [
  { value: 'company', label: 'Юр. лицо' },
  { value: 'ip', label: 'ИП' },
  { value: 'self_employed', label: 'Самозанятый' },
  { value: 'individual', label: 'Физ. лицо' },
];

const STATUS_LABELS = { new: 'Новая', pending: 'На подписи', paid: 'Оплачена', rejected: 'Отклонена' };
const money = (n) => `${Math.round(Number(n || 0)).toLocaleString('ru')} ₽`;

function AttachmentLinks({ attachments }) {
  if (!attachments || attachments.length === 0) return <span className="text-muted">—</span>;
  return (
    <>
      {attachments.map(a => (
        <div key={a.id}>
          <a href={a.url} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()}>
            {a.kind === 'requisites' ? '📎 Реквизиты' : '📎 Счёт/чек'}
          </a>
        </div>
      ))}
    </>
  );
}

function ContractorLedger({ contractorId, onClose, canOpenRequest }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [openRequestId, setOpenRequestId] = useState(null);

  const load = () => {
    api.get(`/contractors/${contractorId}`).then(r => setData(r.data)).catch(() => setError('Не удалось загрузить данные'));
  };
  useEffect(() => { load(); }, [contractorId]);

  if (error) return (
    <div className="modal-overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal"><div className="error-msg">{error}</div><button className="btn-secondary" onClick={onClose}>Закрыть</button></div>
    </div>
  );
  if (!data) return <div className="modal-overlay"><div className="modal">Загрузка...</div></div>;

  const { contractor, summary, requests, entries } = data;

  return (
    <div className="modal-overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 820 }}>
        <div className="modal-header">
          <h2>{contractor.name}</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="text-muted mb-2" style={{ fontSize: 13 }}>
          {TYPES.find(t => t.value === contractor.type)?.label || contractor.type}{contractor.inn ? ` · ИНН ${contractor.inn}` : ''}
        </div>
        <div className="text-muted mb-2" style={{ fontSize: 13 }}>
          {contractor.bank_name && <span>Банк: {contractor.bank_name}. </span>}
          {contractor.account && <span>Счёт: {contractor.account}. </span>}
          {contractor.bik && <span>БИК: {contractor.bik}. </span>}
          {contractor.corr_account && <span>Корр. счёт: {contractor.corr_account}. </span>}
          {contractor.doc_path && (
            <span>Справка о постановке на учёт: <a href={contractor.doc_path} target="_blank" rel="noreferrer">файл</a></span>
          )}
        </div>

        <div className="stats-row">
          <div className="stat-card">
            <div className="stat-value" style={{ color: summary.pendingCount ? 'var(--warning)' : undefined }}>{money(summary.pendingTotal)}</div>
            <div className="stat-label">Ожидает оплаты ({summary.pendingCount})</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{money(summary.paidToContractor)}</div>
            <div className="stat-label">Выплачено ему (факт)</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{money(summary.paidByContractor)}</div>
            <div className="stat-label">Получено от него (факт)</div>
          </div>
          <div className="stat-card" style={{ borderColor: summary.balance >= 0 ? 'var(--success)' : 'var(--danger)' }}>
            <div className="stat-value" style={{ color: summary.balance >= 0 ? 'var(--success)' : 'var(--danger)' }}>
              {summary.balance >= 0 ? '+' : ''}{money(summary.balance)}
            </div>
            <div className="stat-label">{summary.balance >= 0 ? 'Он нам должен / переплатил' : 'Мы ему должны (по факту)'}</div>
          </div>
        </div>

        {requests.length > 0 && (
          <>
            <h3 className="mb-2">Заявки</h3>
            <div className="table-wrap mb-2">
              <table>
                <thead><tr><th>#</th><th>Проект</th><th>Статья</th><th>Сумма</th><th>Статус</th><th>Вложения</th></tr></thead>
                <tbody>
                  {requests.map(r => (
                    <tr key={r.id} className={canOpenRequest ? 'row-clickable' : ''} onClick={() => canOpenRequest && setOpenRequestId(r.id)}>
                      <td>{r.id}</td>
                      <td>{r.project}</td>
                      <td>{r.article}</td>
                      <td>{money(r.amount)}</td>
                      <td><span className={`badge badge-${r.status}`}>{STATUS_LABELS[r.status]}</span></td>
                      <td><AttachmentLinks attachments={r.attachments} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        <h3 className="mb-2">Движения по ДДС</h3>
        {entries.length === 0 ? <div className="empty-state">Пока нет фактических операций</div> : (
          <div className="table-wrap">
            <table>
              <thead><tr><th>Дата</th><th>Тип</th><th>Статья</th><th>Проект</th><th>Сумма</th></tr></thead>
              <tbody>
                {entries.map(e => (
                  <tr key={e.id}>
                    <td>{new Date(e.entry_date).toLocaleDateString('ru')}</td>
                    <td><span className={`badge ${e.direction === 'income' ? 'badge-paid' : 'badge-new'}`}>{e.direction === 'income' ? 'Приход' : 'Расход'}</span></td>
                    <td>{e.article_name || '—'}</td>
                    <td>{e.project_name || '—'}</td>
                    <td>{money(e.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="modal-actions">
          <button className="btn-secondary" onClick={onClose}>Закрыть</button>
        </div>
      </div>
      {openRequestId && <PaymentDetail id={openRequestId} onClose={() => setOpenRequestId(null)} onChanged={load} />}
    </div>
  );
}

export function ContractorModal({ item, onClose, onSaved }) {
  const isEdit = !!item;
  const [name, setName] = useState(item?.name || '');
  const [type, setType] = useState(item?.type || 'ip');
  const [inn, setInn] = useState(item?.inn || '');
  const [bankName, setBankName] = useState(item?.bank_name || '');
  const [bik, setBik] = useState(item?.bik || '');
  const [account, setAccount] = useState(item?.account || '');
  const [corrAccount, setCorrAccount] = useState(item?.corr_account || '');
  const [legalAddress, setLegalAddress] = useState(item?.legal_address || '');
  const [contact, setContact] = useState(item?.contact || '');
  const [file, setFile] = useState(null);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (type === 'self_employed' && !file && !item?.doc_path) {
      setError('Для самозанятого обязательна справка о постановке на учёт');
      return;
    }
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('name', name);
      fd.append('type', type);
      fd.append('inn', inn);
      fd.append('bank_name', bankName);
      fd.append('bik', bik);
      fd.append('account', account);
      fd.append('corr_account', corrAccount);
      fd.append('legal_address', legalAddress);
      fd.append('contact', contact);
      if (file) fd.append('doc', file);
      let saved;
      if (isEdit) saved = await api.put(`/contractors/${item.id}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      else saved = await api.post('/contractors', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      onSaved(saved.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Ошибка сохранения');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <div className="modal-header">
          <h2>{isEdit ? 'Редактировать контрагента' : 'Новый контрагент'}</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        {error && <div className="error-msg">{error}</div>}
        <form onSubmit={submit}>
          <div className="form-row">
            <div className="form-group">
              <label>Название *</label>
              <input value={name} onChange={e => setName(e.target.value)} required />
            </div>
            <div className="form-group">
              <label>Тип *</label>
              <select value={type} onChange={e => setType(e.target.value)}>
                {TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
          </div>
          <div className="form-group">
            <label>ИНН</label>
            <input value={inn} onChange={e => setInn(e.target.value)} />
          </div>
          <div className="form-row">
            <div className="form-group"><label>Банк</label><input value={bankName} onChange={e => setBankName(e.target.value)} /></div>
            <div className="form-group"><label>БИК</label><input value={bik} onChange={e => setBik(e.target.value)} /></div>
          </div>
          <div className="form-row">
            <div className="form-group"><label>Расчётный счёт</label><input value={account} onChange={e => setAccount(e.target.value)} /></div>
            <div className="form-group"><label>Корр. счёт</label><input value={corrAccount} onChange={e => setCorrAccount(e.target.value)} /></div>
          </div>
          <div className="form-group">
            <label>Юридический адрес</label>
            <input value={legalAddress} onChange={e => setLegalAddress(e.target.value)} />
          </div>
          <div className="form-group">
            <label>Контакт</label>
            <input value={contact} onChange={e => setContact(e.target.value)} />
          </div>
          <div className="form-group">
            <label>Справка о постановке на учёт {type === 'self_employed' ? '(обязательно)' : ''}</label>
            <input type="file" accept="image/*,application/pdf" onChange={e => setFile(e.target.files[0] || null)} />
            {item?.doc_path && !file && <div className="text-muted" style={{ fontSize: 12 }}>Уже загружен: <a href={item.doc_path} target="_blank" rel="noreferrer">файл</a></div>}
          </div>
          <div className="modal-actions">
            <button type="button" className="btn-secondary" onClick={onClose}>Отмена</button>
            <button type="submit" className="btn-primary" disabled={saving}>{saving ? 'Сохранение...' : 'Сохранить'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function Contractors() {
  const { user } = useAuth();
  const canEdit = user.role === 'admin' || user.role === 'accountant';
  // Завести контрагента может любой, кто попал в этот раздел (дизайнеры сюда и так не заходят) —
  // например, менеджер прямо перед созданием заявки. Править/удалять — только админ/бухгалтер.
  const canCreate = user.role !== 'designer';
  const canExport = ['admin', 'accountant', 'rop'].includes(user.role);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [ledgerId, setLedgerId] = useState(null);

  const load = () => {
    setLoading(true);
    api.get('/contractors').then(r => setItems(r.data)).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const remove = async (item) => {
    if (!window.confirm(`Удалить контрагента «${item.name}»?`)) return;
    await api.delete(`/contractors/${item.id}`);
    load();
  };

  const exportItems = () => downloadExcel(
    'Контрагенты',
    ['Название', 'Тип', 'ИНН', 'Банк', 'Счёт'],
    items.map(item => [item.name, TYPES.find(t => t.value === item.type)?.label || item.type, item.inn || '—', item.bank_name || '—', item.account || '—'])
  );

  return (
    <div>
      <div className="toolbar">
        <h2>Контрагенты</h2>
        <div className="toolbar-spacer" />
        {canExport && items.length > 0 && <button className="btn-secondary btn-sm" onClick={exportItems}>Экспорт в Excel</button>}
        {canCreate && <button className="btn-primary btn-sm" onClick={() => setShowNew(true)}>+ Новый контрагент</button>}
      </div>
      {loading ? <div className="loading-text">Загрузка...</div> : items.length === 0 ? (
        <div className="empty-state">Контрагентов пока нет</div>
      ) : (
        <div className="table-wrap">
          <table>
            <thead>
              <tr><th>Название</th><th>Тип</th><th>ИНН</th><th>Банк</th><th>Счёт</th>{canEdit && <th></th>}</tr>
            </thead>
            <tbody>
              {items.map(item => (
                <tr key={item.id} className="row-clickable" onClick={() => setLedgerId(item.id)}>
                  <td>{item.name}</td>
                  <td>{TYPES.find(t => t.value === item.type)?.label || item.type}</td>
                  <td>{item.inn || '—'}</td>
                  <td>{item.bank_name || '—'}</td>
                  <td>{item.account || '—'}</td>
                  {canEdit && (
                    <td onClick={e => e.stopPropagation()}>
                      <button className="btn-secondary btn-sm" onClick={() => setEditing(item)}>Изменить</button>{' '}
                      {user.role === 'admin' && <button className="btn-danger btn-sm" onClick={() => remove(item)}>Удалить</button>}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {showNew && <ContractorModal onClose={() => setShowNew(false)} onSaved={() => { setShowNew(false); load(); }} />}
      {editing && <ContractorModal item={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
      {ledgerId && <ContractorLedger contractorId={ledgerId} onClose={() => setLedgerId(null)} canOpenRequest={['admin','accountant','rop'].includes(user.role)} />}
    </div>
  );
}
